import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import styles from "./assign.module.scss";
import { getFacilityLabelByName, renderValue } from "../../../utils";
import type { FacilitiesData, Facility, UpdateFormDetailsPayload } from "../../../features/custom-forms";
import type { ApiEnvelope } from "../../../types/api";

const customerGroupText: Record<string, string> = {
  all: "All Customer",
  postacute: "Post-Acute",
  acute: "Acute",
};

interface ViewAssignProps {
  facilitiesData?: ApiEnvelope<FacilitiesData>;
  updateStatus: ApiEnvelope<UpdateFormDetailsPayload> | Record<string, never>;
  onClearUpdateStatus: () => void;
}

const ViewAssign: React.FC<ViewAssignProps> = ({ facilitiesData, updateStatus, onClearUpdateStatus }) => {
  const formData = useSelector((state: any) => state.forms.formDetailsData);

  useEffect(() => {
    if (!updateStatus?.status) return;
    const timeoutId = setTimeout(() => {
      onClearUpdateStatus();
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [updateStatus?.status, onClearUpdateStatus]);

  const assignedFacilities = useMemo(() => {
    const soldToNumbers = formData?.assignInfo?.organization?.soldToNumbers || [];
    const facilities = facilitiesData?.data?.facilities || [];

    if (!soldToNumbers.length || !facilities.length) return [];

    const shipToSet = new Set(soldToNumbers.flatMap((s: { shipToNumbers: string[] }) => s.shipToNumbers));

    return facilities.filter((f: Facility) => shipToSet.has(f.facilityNumber));
  }, [formData, facilitiesData]);

  return (
    <>
      {updateStatus && updateStatus?.status === "success" && <div className={`alert alert-success ${styles.form_alert}`}>Successfully updated</div>}
      {updateStatus && updateStatus?.status === "error" && <div className={`alert alert-danger ${styles.form_alert}`}>Assign Form not update</div>}
      {formData?.assignInfo?.feature !== "NE1 WMS" && (
        <div className={`col-12 ${styles.col_spec}`}>
          <label className={`col-3 col-form-label ${styles.label_spec}`}>Customer Group Selection:</label>
          <div className="col-3 p-0">
            <span className={`col-form-label d-inline-block ${styles.content_spec}`}>
              {renderValue(customerGroupText?.[formData?.assignInfo?.customerGroup])}
            </span>
          </div>
        </div>
      )}
      {formData?.assignInfo?.feature === "Survey" && (
        <div className={`col-12 ${styles.col_spec}`}>
          <label className={`col-3 col-form-label ${styles.label_spec}`}>Email:</label>
          <div className="col-9 p-0">
            {Array.isArray(formData?.assignInfo?.userEmail) && formData.assignInfo.userEmail.length > 0 ? (
              <span className={`col-form-label ${styles.content_spec}`}>{formData.assignInfo.userEmail.join(", ")}</span>
            ) : (
              "-"
            )}
          </div>
        </div>
      )}
      <div className={`col-12 ${styles.col_spec}`}>
        <label className={`col-3 col-form-label ${styles.label_spec}`}>Customer Number:</label>
        <div className="col-3 p-0">
          <span className={`col-form-label d-inline-block ${styles.content_spec}`}>{renderValue(formData?.assignInfo?.customerNumber)}</span>
        </div>
      </div>
      <div className={`col-12 ${styles.col_spec}`}>
        <label className={`col-3 col-form-label ${styles.label_spec}`}>Organization:</label>
        <div className="col-7 p-0">
          {assignedFacilities.length > 0
            ? assignedFacilities.map((facility: Facility) => (
                <div key={facility.facilityNumber} className={`col-form-label ${styles.content_spec}`}>
                  {getFacilityLabelByName(facility)}
                </div>
              ))
            : "-"}
        </div>
      </div>
      <div className={`col-12 ${styles.col_spec}`}>
        <label className={`col-3 col-form-label ${styles.label_spec}`}>Feature:</label>
        <div className="col-3 p-0">
          <span className={`col-form-label d-inline-block ${styles.content_spec}`}>{renderValue(formData?.assignInfo?.feature)}</span>
        </div>
      </div>
      <div className={`col-12 ${styles.col_spec}`}>
        <label className={`col-3 col-form-label ${styles.label_spec}`}>Sub-Feature:</label>
        <div className="col-3 p-0">
          <span className={`col-form-label d-inline-block ${styles.content_spec}`}>{renderValue(formData?.assignInfo?.subFeature)}</span>
        </div>
      </div>
      <div className={`col-12 ${styles.col_spec}`}>
        <label className={`col-3 col-form-label ${styles.label_spec}`}>Sequence:</label>
        <div className="col-3 p-0">
          <span className={`col-form-label d-inline-block ${styles.content_spec}`}>{renderValue(formData?.assignInfo?.sequence)}</span>
        </div>
      </div>
    </>
  );
};

export default ViewAssign;
